import React, { useCallback, useMemo } from 'react';
import { Stack, Box, Typography, Grid2 as Grid } from '@mui/material';
import {
  CloseFormShowButton,
  EditFormShowButton,
  SaveFormShowButton,
  useFormContext,
  useFormShowContext,
} from 'framework/form';
import { useAuthorization } from 'framework/authorization';
import { RECRUITING_CANDIDATE_UPDATE } from 'domain/shared/candidate';
import { useShowContext } from 'framework/show';
import { Stages } from 'domain/shared/candidate';
import { usePositionService, PositionType } from 'domain/shared/employee';
import { CalculatePercentileModalButton } from 'domain/shared/performanceMeasurement';
import { useAsyncValue } from 'framework/utils';
import { isStatusChangeCommentFilled, isStatusChangeCommentRequired } from '../statusChangeHelper';

// Components
import CandidateTimeline from './CandidateTimelineSection/CandidateTimeline';
import CandidateAvatar from './CandidateAvatar/CandidateAvatar';
import ProfileDetails from './ProfileDetails/ProfileDetails';
import CandidateApplicationSectionWrapper from './CandidateApplicationSectionWrapper/CandidateApplicationSectionWrapper';
import CandidateStepsStatuses from './CandidateStepsStatuses/CandidateStepsStatuses';
import SkillsBadges from './Badges/SkillsBadges/SkillsBadges';
import ApplicationsHistory from './ApplicationsHistory';
import CandidateProfileEditFields from './shared/CandidateProfileEditFields';
import { ContactEmailField, ContactPhoneField, ProfileCard, EntityProfileLayout } from 'framework/ui/layout';
import { CandidateDuplicateOfField } from '../field/CandidateDuplicateOfField';

interface Specialization {
  specialization: string;
  technology?: string | null;
  qualificationLevel: string;
  isConfirmed: boolean;
}

const CalculatePercentileButtonWithCandidatePosition: React.FC = () => {
  const { data } = useShowContext();
  const { findAll: findAllPositions } = usePositionService();
  const positions = useAsyncValue(findAllPositions);

  const vacancyData = data?.lastAppliedVacancyData;

  const initialPosition = useMemo(() => {
    if (!vacancyData?.specialization || !positions?.length) {
      return null;
    }
    const position = positions.find((p: PositionType) => p.name === vacancyData.specialization);
    if (!position) {
      return null;
    }
    const technology = vacancyData.technology;
    return position.id ? `${position.id}${technology ? `--${technology}` : ''}` : null;
  }, [vacancyData, positions]);
  return <CalculatePercentileModalButton initialPosition={initialPosition} />;
};

const CandidateProfileContactDetails = () => {
  const { data } = useShowContext();

  const hasConfirmedSpecialization = useMemo(
    () => data?.specializations?.some((spec: Specialization) => spec.isConfirmed) ?? false,
    [data?.specializations],
  );

  return (
    <Stack direction="column" gap="0.5rem">
      {data?.qualificationLevel && (
        <Box display="flex" alignItems="flex-start" gap={1}>
          <Typography variant="body1">
            {hasConfirmedSpecialization ? 'Confirmed Level:' : 'Unconfirmed Level:'}
          </Typography>
          <Typography variant="body1">
            <strong>{data.qualificationLevel}</strong>
          </Typography>
        </Box>
      )}

      <ContactEmailField source="personalEmail" />
      <ContactEmailField source="otherEmail" />
      <ContactPhoneField source="personalPhoneNumber" />
      <ContactPhoneField source="otherPhoneNumber" />

      {data?.duplicateOf && data.duplicateOf.id ? (
        <CandidateDuplicateOfField
          duplicateOfId={data.duplicateOf.id}
          duplicateFullName={data.duplicateOf.name.fullName}
        />
      ) : null}
    </Stack>
  );
};

const CandidateProfile = () => {
  const { isGranted } = useAuthorization();
  const { isEditMode } = useFormShowContext();
  const { data } = useShowContext();
  const { data: formData = {} } = useFormContext();

  const renderActionButtons = useCallback(() => {
    if (!isGranted(RECRUITING_CANDIDATE_UPDATE)) {
      return null;
    }

    return (
      <Grid sx={{ margin: '-0.5rem -0.5rem 0.5rem 0.5rem' }}>
        {isEditMode ? (
          <>
            <SaveFormShowButton
              disabled={isStatusChangeCommentRequired(formData) && !isStatusChangeCommentFilled(formData)}
            />

            <CloseFormShowButton />
          </>
        ) : (
          <EditFormShowButton />
        )}
      </Grid>
    );
  }, [isGranted, isEditMode, formData]);

  const contentSections = [
    <>
      <CandidateApplicationSectionWrapper />
      <CandidateStepsStatuses />
    </>,
    <CandidateProfileContactDetails />,
    <SkillsBadges key="skills" />,
  ];

  if (data?.previousApplications && data.previousApplications.length > 0) {
    contentSections.push(<ApplicationsHistory key="history" applications={data.previousApplications} />);
  }

  const isScreeningStage = data?.lastActiveApplication?.applicationPipelineState?.stage === Stages.Screening;

  if (isScreeningStage) {
    contentSections.push(
      <Box key="percentile" display="flex" justifyContent="flex-end">
        <CalculatePercentileButtonWithCandidatePosition />
      </Box>,
    );
  }

  return (
    <EntityProfileLayout
      leftColumnElement={
        <ProfileCard
          actionButtons={renderActionButtons()}
          avatarSection={<CandidateAvatar />}
          primaryDetails={<ProfileDetails />}
          contentSections={contentSections}
          fullSizeContent={isEditMode ? <CandidateProfileEditFields /> : undefined}
        />
      }
      rightColumnElement={<CandidateTimeline />}
    />
  );
};

export default CandidateProfile;
